import { useEffect } from "react";
import { WithTranslation, withTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import styled from "styled-components";
import { ThunkDispatch } from "core-integration/src/store";
import { getElementTypesById } from "core-integration/src/store/element-types/reducers";
import {
  getElementControls,
  getElementsFromControls,
} from "core-integration/src/store/elements/reducers";
import { updateElementControlsAndMaxCost } from "core-integration/src/store/elements/thunks";
import { getTeamsById } from "core-integration/src/store/teams/reducers";
import BoldLink from "../BoldLink";
import ElementDialogButton from "../ElementDialogButton";
import ElementShirt from "../ElementShirt";
import { ElementRow, ElementTable, PrimaryCell } from "../ElementTable";
import Panel from "../Panel";
import SubHeading from "../SubHeading";
import { Ellipsis } from "../Utils";
import StatusPanel from "./StatusPanel";

const AvailabilityTable = styled(ElementTable)`
  th:not(:nth-child(3)) {
    text-align: center;
  }

  th {
    &:first-of-type {
      border-top-left-radius: ${({ theme }) => theme.radii[2]};
    }

    &:last-of-type {
      border-top-right-radius: ${({ theme }) => theme.radii[2]};
    }
  }
`;

const StatusCol = styled.th`
  width: 12%;
`;

const ShirtCol = styled.th`
  width: 16%;
`;

const PlayerCol = styled.th`
  width: 36%;
`;

const PosCol = styled.th`
  width: 20%;
`;

const ClubCol = styled.th`
  width: 20%;
`;

const ShirtCell = styled.td`
  text-align: right;
`;

type Props = WithTranslation;

const PlayerAvailability = ({ t }: Props) => {
  const dispatch = useDispatch<ThunkDispatch>();
  const controls = useSelector(getElementControls);
  const elements = useSelector(getElementsFromControls);
  const elementTypesById = useSelector(getElementTypesById);
  const teamsById = useSelector(getTeamsById);

  useEffect(() => {
    dispatch(
      updateElementControlsAndMaxCost({
        ...controls,
        filter: "all",
        sort: "news_added",
        search: "",
      }),
    );
  }, []);

  return (
    <StatusPanel>
      <StatusPanel.Header
        link={
          <BoldLink to="/the-scout/player-news">
            {t("playerAvailability.more", "more")}
          </BoldLink>
        }
      >
        <SubHeading>
          {t("playerAvailability.heading", "Player Availability")}
        </SubHeading>
      </StatusPanel.Header>
      <Panel>
        <StatusPanel.Body>
          <AvailabilityTable>
            <thead>
              <tr>
                <StatusCol>&nbsp;</StatusCol>
                <ShirtCol>&nbsp;</ShirtCol>
                <PlayerCol>
                  {t("playerAvailability.player", "Player")}
                </PlayerCol>
                <PosCol>{t("playerAvailability.postitionShort", "Pos")}</PosCol>
                <ClubCol>{t("playerAvailability.club", "Club")}</ClubCol>
              </tr>
            </thead>
            <tbody>
              {elements.data.slice(0, 16).map((e) => (
                <ElementRow key={e.id}>
                  <td>
                    <ElementDialogButton elementId={e.id} variant="list" />
                  </td>
                  <ShirtCell>
                    <ElementShirt elementId={e.id} sizes="24px" />
                  </ShirtCell>
                  <td>
                    <Ellipsis>{e.web_name}</Ellipsis>
                  </td>
                  <PrimaryCell>
                    {elementTypesById[e.element_type].singular_name_short}
                  </PrimaryCell>
                  <PrimaryCell>{teamsById[e.team].short_name}</PrimaryCell>
                </ElementRow>
              ))}
            </tbody>
          </AvailabilityTable>
        </StatusPanel.Body>
      </Panel>
    </StatusPanel>
  );
};

export default withTranslation()(PlayerAvailability);
